import { enhancedApi } from '@store/generated/enrollments';

/**
 * Feature-specific tag types for enrollment cache management
 */
const ENROLLMENT_TAG_TYPES = ['Enrollment', 'EnrollmentList'] as const;

/**
 * Enrollments API - extends the generated OpenAPI client
 * This ensures we're using generated types and endpoints
 * while maintaining feature-specific customization
 */
export const enrollmentsApi = enhancedApi.enhanceEndpoints({
  addTagTypes: ENROLLMENT_TAG_TYPES,
  endpoints: {
    // Admin endpoints
    getV2AdminOrgsByOrganizationIdEnrollments: {
      providesTags: (result, _error, arg) =>
        result?.enrollments
          ? [
              ...result.enrollments.map(({ stedi_transaction_enrollment_id }) => ({
                type: 'Enrollment' as const,
                id: stedi_transaction_enrollment_id || 'unknown',
              })),
              { type: 'EnrollmentList' as const, id: arg.organizationId },
            ]
          : [{ type: 'EnrollmentList', id: arg.organizationId }],
    },

    getV2AdminOrgsByOrganizationIdEnrollmentsAndEnrollmentId: {
      providesTags: (_result, _error, arg) => [{ type: 'Enrollment', id: arg.enrollmentId }],
    },

    postV2AdminOrgsByOrganizationIdEnrollments: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'EnrollmentList', id: arg.organizationId },
      ],
    },

    postV2AdminOrgsByOrganizationIdEnrollmentsAndEnrollmentIdSync: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Enrollment', id: arg.enrollmentId }],
    },

    postV2AdminOrgsByOrganizationIdEnrollmentsSyncAll: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'EnrollmentList', id: arg.organizationId },
      ],
    },

    // Provider endpoints
    getV2ProviderEnrollments: {
      providesTags: result =>
        result?.enrollments
          ? [
              ...result.enrollments.map(({ stedi_transaction_enrollment_id }) => ({
                type: 'Enrollment' as const,
                id: stedi_transaction_enrollment_id || 'unknown',
              })),
              { type: 'EnrollmentList' as const, id: 'PROVIDER' },
            ]
          : [{ type: 'EnrollmentList', id: 'PROVIDER' }],
    },

    getV2ProviderEnrollmentsByEnrollmentId: {
      providesTags: (_result, _error, arg) => [{ type: 'Enrollment', id: arg.enrollmentId }],
    },

    postV2ProviderEnrollments: {
      invalidatesTags: [
        { type: 'EnrollmentList', id: 'PROVIDER' },
      ],
    },

    postV2ProviderEnrollmentsByEnrollmentIdSync: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Enrollment', id: arg.enrollmentId }],
    },

    postV2ProviderEnrollmentsSyncAll: {
      invalidatesTags: [{ type: 'EnrollmentList', id: 'PROVIDER' }],
    },
  },
});

// Export hooks with user-friendly names only (Provider endpoints)
export const {
  // Provider queries
  useGetV2ProviderEnrollmentsQuery: useGetEnrollmentsQuery,
  useLazyGetV2ProviderEnrollmentsQuery: useLazyGetEnrollmentsQuery,
  useGetV2ProviderEnrollmentsByEnrollmentIdQuery: useGetEnrollmentQuery,
  useLazyGetV2ProviderEnrollmentsByEnrollmentIdQuery: useLazyGetEnrollmentQuery,

  // Provider mutations
  usePostV2ProviderEnrollmentsMutation: useInitiateEnrollmentMutation,
  usePostV2ProviderEnrollmentsByEnrollmentIdSyncMutation: useSyncEnrollmentMutation,
  usePostV2ProviderEnrollmentsSyncAllMutation: useSyncAllEnrollmentsMutation,

  // Admin queries
  useGetV2AdminOrgsByOrganizationIdEnrollmentsQuery: useGetEnrollmentsAdminQuery,
  useLazyGetV2AdminOrgsByOrganizationIdEnrollmentsQuery: useLazyGetEnrollmentsAdminQuery,
  useGetV2AdminOrgsByOrganizationIdEnrollmentsAndEnrollmentIdQuery: useGetEnrollmentAdminQuery,
  useLazyGetV2AdminOrgsByOrganizationIdEnrollmentsAndEnrollmentIdQuery:
    useLazyGetEnrollmentAdminQuery,

  // Admin mutations
  usePostV2AdminOrgsByOrganizationIdEnrollmentsMutation: useInitiateEnrollmentAdminMutation,
  usePostV2AdminOrgsByOrganizationIdEnrollmentsAndEnrollmentIdSyncMutation:
    useSyncEnrollmentAdminMutation,
  usePostV2AdminOrgsByOrganizationIdEnrollmentsSyncAllMutation: useSyncAllEnrollmentsAdminMutation,
} = enrollmentsApi;

/**
 * Custom provider mutation: complete a PROVIDER_ACTION_REQUIRED task in-app.
 * Injected here (rather than generated) because the endpoint post-dates the
 * current OpenAPI snapshot. enrollmentId is the local
 * stedi_transaction_enrollment_id; taskId is Stedi's task id.
 */
export const enrollmentTasksApi = enrollmentsApi.injectEndpoints({
  endpoints: builder => ({
    completeEnrollmentTask: builder.mutation<
      unknown,
      { enrollmentId: string; taskId: string; values: Array<Record<string, unknown>> }
    >({
      query: ({ enrollmentId, taskId, values }) => ({
        url: `/v2/provider/enrollments/${enrollmentId}/tasks/${taskId}`,
        method: 'POST',
        body: { values },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Enrollment', id: arg.enrollmentId },
        { type: 'EnrollmentList', id: 'PROVIDER' },
      ],
    }),

    // Upload a document for a DOCUMENT-type task field. Returns the Stedi
    // document_id to reference as { key, documentId } when completing the task.
    uploadEnrollmentDocument: builder.mutation<
      { document_id: string },
      { enrollmentId: string; fileName: string; contentType: string; data: string }
    >({
      query: ({ enrollmentId, fileName, contentType, data }) => ({
        url: `/v2/provider/enrollments/${enrollmentId}/documents`,
        method: 'POST',
        body: { file_name: fileName, content_type: contentType, data },
      }),
    }),
  }),
});

export const { useCompleteEnrollmentTaskMutation, useUploadEnrollmentDocumentMutation } =
  enrollmentTasksApi;
